"use client";

import { useEffect, useState } from "react";
import { useParams, useRouter } from "next/navigation";
import Image from "next/image";
import { useSession } from "@/lib/auth-react";
import { Loading } from "@/components/UIs/Loading";
import { toReadableFormat } from "@/utils/readableFormat";
import { calculateTotalActiveCredits } from "@/utils/bookingUtils";
import { notifyCreditsChanged } from "@/utils/creditEvents";
import { HeartIcon as OutlineHeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as SolidHeartIcon } from "@heroicons/react/24/solid";
import { FaCheck, FaX } from "react-icons/fa6";
import { IoIosArrowDown } from "react-icons/io";
import {
  format,
  isSameDay,
  differenceInMinutes,
  subMonths,
  addMonths,
  isAfter,
  isBefore as dateFnsIsBefore,
  startOfWeek,
  addDays,
  startOfMonth,
  isSameMonth,
} from "date-fns";
import { formatTime } from "@/utils/formatTime";
import BookingComponent from "@/components/FindTeacher/BookingComponent";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginFormInputs, loginSchema } from "@/types/zodSchema";
import { FaExclamation } from "react-icons/fa6";
import LoginModal from "@/components/FindTeacher/LoginModal";
import { Spinner } from "@/components/UIs/Spinner";
import { addToFavorites, removeFromFavorites } from "@/utils/favoriteUtils";
import Link from "next/link";
import ConfirmBookingModal from "@/components/FindTeacher/ConfirmBookingModal";
import PackageLoginModal from "@/components/Pages/Packages/PackageLoginModal";
import { useMemo } from "react";
import { authFetch } from "@/utils/auth-fetch";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export interface Teacher {
  id: string;
  name: string;
  profileLink: string;
  teacherIntroVideoId: string;
  email: string;
  gender: string | null;
  dob: string | null;
  country: string | null;
  nativeLanguage: string | null;
  englishLevel: string;
  arabicLevel: string;
  aboutMe: string | null;
  quote: string | null;
  focuses: { name: string }[];
  spokenLanguages: { language: string }[];
  teacherLevels: { level: string }[];
  rating: number | null;
  image: string | null;
  payoutConfigured?: boolean;
}

interface Availability {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isDisabled?: boolean;
  disabledReason?: string;
}

interface Appointment {
  startTime: string;
  endTime: string;
  date: string;
  status?: string;
  rescheduledToId?: string | null;
}

interface Credit {
  amount: number;
  expiryDate: Date;
  status: "ACTIVE" | "EXPIRED";
}

interface Review {
  id: string;
  text: string;
  student: {
    name: string;
    image?: string;
  };
  date: string;
  rating: number;
}

export default function ATeacherPage() {
  const { data: session } = useSession();
  const router = useRouter();
  const { teacher } = useParams();
  const [teacherData, setTeacherData] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filters, setFilters] = useState<{
    level: boolean;
    duration: boolean;
    availability: boolean;
    country: boolean;
    focus: boolean;
    speaks: boolean;
    sort: boolean;
  }>({
    level: false,
    duration: false,
    availability: false,
    country: false,
    focus: false,
    speaks: false,
    sort: false,
  });
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [studentAppointments, setStudentAppointments] = useState<Appointment[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const today = useMemo(() => new Date(), []);
  const [selectedDay, setSelectedDay] = useState<Date>(today);
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(today));
  const [currentStartDate, setCurrentStartDate] = useState<Date>(
    startOfWeek(today, { weekStartsOn: 0 }),
  );
  const [studentCredits, setStudentCredits] = useState<Credit[]>([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [otherTeachers, setOtherTeachers] = useState<Teacher[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [errorLikeMessage, setErrorLikeMessage] = useState<string | null>(null);
  const [isVerificationError, setIsVerificationError] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isCurrentTeacherFavorite, setIsCurrentTeacherFavorite] =
    useState(false);
  const [otherFavorites, setOtherFavorites] = useState<Record<string, boolean>>(
    {},
  );
  const [selectedFilterRatings, setSelectedFilterRatings] = useState<number[]>(
    [],
  );

  const filteredReviews = reviews.filter(
    (review) =>
      selectedFilterRatings.length === 0 ||
      selectedFilterRatings.includes(review.rating),
  );

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setShowConfirmModal(true);
  };

  useEffect(() => {
    if (session?.user?.role === "TEACHER") {
      router.push("/dashboard");
    }
  }, [session?.user?.role, router]);

  useEffect(() => {
    if (window.location.hash === "#book") {
      const timeout = setTimeout(() => {
        const el = document.getElementById("book");
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Handle Like and Unlike
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!session?.user.id || !session?.accessToken) return;

      try {
        // Check if the current teacher is a favorite
        if (teacherData?.id) {
          const response = await authFetch("/api/favorites/check", session.accessToken!, {
            method: "POST",
            body: JSON.stringify({
              teacherId: teacherData.id,
            }),
          });
          if (response.ok) {
            const data = await response.json();
            setIsCurrentTeacherFavorite(data.data?.isFavorite ?? data.isFavorite);
          }
        }

        // Check favorites for other teachers
        if (otherTeachers.length) {
          const response = await authFetch("/api/favorites/check-multiple", session.accessToken!, {
            method: "POST",
            body: JSON.stringify({
              teacherIds: otherTeachers.map((t) => t.id),
            }),
          });
          if (response.ok) {
            const data = await response.json();
            const favorites = data.data?.favorites ?? [];
            const favMap = Array.isArray(favorites)
              ? favorites.reduce(
                  (acc: Record<string, boolean>, fav: { teacherId: string; isFavorite: boolean }) => {
                    acc[fav.teacherId] = fav.isFavorite;
                    return acc;
                  },
                  {},
                )
              : {};
            setOtherFavorites(favMap);
          }
        }
      } catch (error) {
        console.error("Failed to fetch favorites:", error);
      }
    };

    fetchFavorites();
  }, [session?.user.id, session?.accessToken, teacherData?.id, otherTeachers]);

  const handleCurrentTeacherFavoriteToggle = async () => {
    if (!session?.user.id || !session?.accessToken || !teacherData?.id) {
      triggerErrorLike("You need to be logged in to favorite a teacher.");
      return;
    }

    try {
      if (isCurrentTeacherFavorite) {
        await removeFromFavorites(teacherData.id, session.accessToken);
        setIsCurrentTeacherFavorite(false);
      } else {
        await addToFavorites(teacherData.id, session.accessToken);
        setIsCurrentTeacherFavorite(true);
      }
    } catch (error) {
      console.error("Error toggling favorite for current teacher:", error);
      alert("Failed to update favorite status. Please try again.");
    }
  };

  // Fetch favorite statuses for "Might Also Like" teachers
  useEffect(() => {
    const fetchFavoriteStatuses = async () => {
      if (!session?.user?.id || !session?.accessToken || otherTeachers.length === 0) return;

      try {
        const response = await authFetch("/api/favorites/check-multiple", session.accessToken!, {
          method: "POST",
          body: JSON.stringify({
            teacherIds: otherTeachers.map((teacher) => teacher.id),
          }),
        });

        if (!response.ok) {
          console.error(
            "Failed to fetch favorite statuses for other teachers.",
          );
          return;
        }

        const data = await response.json();
        const favorites = data.data?.favorites ?? [];
        const favMap = Array.isArray(favorites)
          ? favorites.reduce(
              (acc: Record<string, boolean>, fav: { teacherId: string; isFavorite: boolean }) => {
                acc[fav.teacherId] = fav.isFavorite;
                return acc;
              },
              {},
            )
          : {};
        setOtherFavorites(favMap);
      } catch (error) {
        console.error("Error fetching favorite statuses:", error);
      }
    };

    fetchFavoriteStatuses();
  }, [session?.user?.id, session?.accessToken, otherTeachers]);

  const handleOtherTeacherFavoriteToggle = async (teacherId: string) => {
    if (!session?.user.id || !session?.accessToken || !teacherId) {
      triggerErrorLike("You need to be logged in to favorite a teacher.");
      return;
    }

    try {
      if (otherFavorites[teacherId]) {
        await removeFromFavorites(teacherId, session.accessToken);
        setOtherFavorites((prev) => ({ ...prev, [teacherId]: false }));
      } else {
        await addToFavorites(teacherId, session.accessToken);
        setOtherFavorites((prev) => ({ ...prev, [teacherId]: true }));
      }
    } catch (error) {
      console.error("Error toggling favorite for teacher:", error);
      alert("Failed to update favorite status. Please try again.");
    }
  };

  // PR 3 (#619): credentials login is gone — the LoginModal component
  // now drives the Google OAuth handshake itself when opened.

  const triggerError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage(null);
    }, 5000);
  };

  const triggerErrorLike = (message: string) => {
    setErrorLikeMessage(message);
    setTimeout(() => {
      setErrorLikeMessage(null);
    }, 5000);
  };

  const resendVerificationCode = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsVerificationError(false);
    router.push("/auth/resend-verify-email");
  };

  // Fetch other Teachers
  useEffect(() => {
    const fetchAllTeachers = async () => {
      try {
        const response = await fetch(`/api/teachers/active`);
        if (!response.ok) throw new Error("Failed to fetch teachers");
        const json = await response.json();

        // Convert `teacher` from URL params to string and normalize it
        const currentProfileLink = decodeURIComponent(String(teacher))
          .toLowerCase()
          .trim();

        // Filter out the current teacher
        const filteredTeachers = json.data.filter((t: Teacher) => {
          const teacherProfileLink = t.profileLink?.toLowerCase().trim(); // Normalize profile links
          return teacherProfileLink !== currentProfileLink;
        });

        setOtherTeachers(filteredTeachers);
      } catch (error) {
        console.error("Error fetching all teachers:", error);
      }
    };

    fetchAllTeachers();
  }, [teacher]);

  // Fetch Teacher Reviews
  useEffect(() => {
    const fetchReviews = async () => {
      if (!teacherData?.id) return;

      try {
        const response = await fetch(`/api/teachers/${teacherData.id}/reviews`);
        if (!response.ok) throw new Error("Failed to fetch reviews");

        const json = await response.json();
        setReviews(json.data.reviews || []);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    fetchReviews();
  }, [teacherData]);
  const numberOfReviews = reviews.length;

  // Toggle Filters
  const toggleFilter = (filter: keyof typeof filters) => {
    setFilters((prev) => {
      const newFilters = Object.keys(prev).reduce(
        (acc, key) => {
          acc[key as keyof typeof filters] =
            key === filter ? !prev[filter] : false;
          return acc;
        },
        {} as typeof filters,
      );
      return newFilters;
    });
  };

  // Handle Click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterDropdowns = document.querySelectorAll(".filter-dropdown");
      let isClickInside = false;

      // Check if the click happened inside any dropdown
      filterDropdowns.forEach((dropdown) => {
        if (dropdown.contains(event.target as Node)) {
          isClickInside = true;
        }
      });

      // If the click happened outside, close all filters
      if (!isClickInside) {
        setFilters((prev) =>
          Object.keys(prev).reduce(
            (acc, key) => ({ ...acc, [key]: false }),
            {} as typeof filters,
          ),
        );
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch This Teacher Data
  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        const response = await fetch(`/api/teachers/profile/${teacher}`);
        if (!response.ok) throw new Error("Failed to fetch teacher data");
        const json = await response.json();
        setTeacherData(json.data);

        // Fetch availability after teacher data is fetched
        if (json.data.id) {
          await fetchAvailability(json.data.id);
        }
      } catch (error) {
        console.error("Error fetching teacher data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchStudentCredits = async () => {
      if (!session?.accessToken) return;
      try {
        const response = await authFetch(`/api/credits`, session.accessToken!);
        if (!response.ok) return;
        const json = await response.json();
        setStudentCredits(json.data.active);
      } catch (error) {
        console.error("Failed to fetch student credits:", error);
      }
    };

    fetchTeacherData();
    fetchStudentCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, teacher]);

  const fetchAvailability = async (teacherId: string) => {
    setAvailabilityLoading(true);
    try {
      const requests: Promise<Response>[] = [
        fetch(`/api/teachers/${teacherId}/availability?timezone=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`),
        fetch(`/api/teachers/${teacherId}/booked-appointments`),
      ];

      if (session?.accessToken) {
        requests.push(
          authFetch("/api/appointments/student", session.accessToken!),
        );
      }

      const [availabilityResponse, appointmentsResponse, studentAppointmentsResponse] =
        await Promise.all(requests);

      const availabilityJson = await availabilityResponse.json();
      const appointmentsJson = await appointmentsResponse.json();

      setAvailability(availabilityJson.data);
      setAppointments(appointmentsJson.data);
      if (studentAppointmentsResponse) {
        const studentAppointmentsJson = await studentAppointmentsResponse.json();
        setStudentAppointments(studentAppointmentsJson.data ?? []);
      } else {
        setStudentAppointments([]);
      }
    } catch (error) {
      console.error("Failed to fetch availability or appointments:", error);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => {
      const newMonth = subMonths(prev, 1);
      const firstDayOfNewMonth = startOfMonth(newMonth);

      // If returning to the current month, start from today's week
      const newStartDate = isSameMonth(newMonth, new Date())
        ? startOfWeek(new Date(), { weekStartsOn: 0 }) // Set to today's week
        : startOfWeek(firstDayOfNewMonth, { weekStartsOn: 0 }); // Otherwise, set to new month's first week's start

      setCurrentStartDate(newStartDate);

      // Select today if in the current month, otherwise select the first day of the new month
      setSelectedDay(
        isSameMonth(newMonth, new Date()) ? new Date() : firstDayOfNewMonth,
      );

      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      const newMonth = addMonths(prev, 1);
      const firstDayOfNewMonth = startOfMonth(newMonth);
      setCurrentStartDate(startOfWeek(firstDayOfNewMonth, { weekStartsOn: 0 }));
      setSelectedDay(firstDayOfNewMonth);
      return newMonth;
    });
  };

  const handlePreviousWeek = () => {
    const previousWeekStart = addDays(currentStartDate, -7);
    setCurrentStartDate(previousWeekStart);
    setCurrentMonth(startOfMonth(previousWeekStart));
  };

  const handleNextWeek = () => {
    setCurrentStartDate((prev) => {
      const nextWeekStart = addDays(prev, 7);
      setCurrentMonth(startOfMonth(nextWeekStart));
      return nextWeekStart;
    });
  };

  const handlePreviousDay = () => {
    if (!selectedDay) return;
    const previousDay = addDays(selectedDay, -1);
    setSelectedDay(previousDay);
  };

  const handleNextDay = () => {
    if (!selectedDay) return;
    const nextDay = addDays(selectedDay, 1);
    setSelectedDay(nextDay);
  };

  useEffect(() => {
    if (!selectedDay) {
      setSelectedDay(today);
    }
  }, [selectedDay, today]);

  const calculateTotalCredits = () =>
    calculateTotalActiveCredits(studentCredits);

  const handleBookingFlow = () => {
    if (!session) {
      setShowLoginModal(true);
    } else {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmBooking = async () => {
    if (bookingInProgress) return;
    if (!selectedTimeSlot || !selectedDay) {
      alert("Please select a time slot and a date.");
      return;
    }

    const selectedSlot = availability.find(
      (slot) => slot.id === selectedTimeSlot,
    );
    if (!selectedSlot) {
      alert("Invalid time slot selected.");
      return;
    }

    const startDateTime = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate(),
      ...selectedSlot.startTime.split(":").map(Number),
    );

    const endDateTime = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate(),
      ...selectedSlot.endTime.split(":").map(Number),
    );

    const durationInMinutes = differenceInMinutes(endDateTime, startDateTime);
    const totalCredits = calculateTotalCredits();

    // Show error alert if credits are insufficient
    if (totalCredits < durationInMinutes) {
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000); // Hide error alert after 5 seconds
      return;
    }

    setBookingInProgress(true);
    try {
      const response = await authFetch("/api/appointments", session?.accessToken!, {
        method: "POST",
        body: JSON.stringify({
          teacherId: teacherData?.id,
          studentId: session?.user.id,
          date: startDateTime.toISOString(),
          durationInMinutes,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const newAppointmentId = responseData.data?.id;
        // Booking succeeded
        setShowConfirmModal(false); // Close the confirmation modal
        setShowSuccessModal(true); // Show success modal
        // Tell the navbar credit bubble to refetch (#644).
        notifyCreditsChanged();
        setTimeout(() => router.push(newAppointmentId ? `/dashboard/bookings#booking-${newAppointmentId}` : "/dashboard/bookings"), 3000); // Redirect to bookings dashboard

        // Create notifications for both the student and the teacher
        await Promise.all([
          // Notification for the student - CREDITS deduction
          authFetch("/api/notifications", session?.accessToken!, {
            method: "POST",
            body: JSON.stringify({
              userId: session?.user.id,
              type: "CREDITS",
              message: `- ${durationInMinutes} minutes have been deducted from your account on ${format(
                new Date(),
                "hh:mm a",
              )} due to session booking.`,
            }),
          }),
          // Notification for the teacher - SESSIONS booking
          authFetch("/api/notifications", session?.accessToken!, {
            method: "POST",
            body: JSON.stringify({
              userId: teacherData?.id,
              type: "SESSIONS",
              message: `You have a new "${durationInMinutes} minutes" session booked by a student on "${format(
                startDateTime,
                "dd/MM/yyyy",
              )}".`,
            }),
          }),
          // Notification for the student - SESSIONS booking confirmation
          authFetch("/api/notifications", session?.accessToken!, {
            method: "POST",
            body: JSON.stringify({
              userId: session?.user.id,
              type: "SESSIONS",
              message: `You have booked a ${durationInMinutes} minutes session on ${format(
                startDateTime,
                "dd/MM/yyyy",
              )} with ${teacherData?.name}.`,
            }),
          }),
        ]);
      } else {
        const errorData = await response.json();
        console.error("Error booking appointment:", errorData);
        alert("Failed to book the appointment.");
      }
    } catch (error) {
      console.error("Error confirming booking:", error);
      alert("An error occurred while booking.");
    } finally {
      setBookingInProgress(false);
    }
  };

  const getAvailableSlotsForDay = (day: Date) => {
    return availability
      .filter((slot) => slot.dayOfWeek === day.getDay())
      .filter((slot) => {
        const slotStartDateTime = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          ...slot.startTime.split(":").map(Number),
        );
        const slotEndDateTime = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          ...slot.endTime.split(":").map(Number),
        );

        const teacherConflict = appointments.some((appointment) => {
          const appointmentDate = new Date(appointment.date);
          const appointmentEnd = new Date(appointment.endTime);
          return (
            isSameDay(appointmentDate, day) &&
            dateFnsIsBefore(slotStartDateTime, appointmentEnd) &&
            isAfter(slotEndDateTime, appointmentDate)
          );
        });

        const studentConflict = studentAppointments.some((appointment) => {
          // Block all non-terminal statuses. NOT_STARTED / STARTED /
          // COMPLETED (mid-flight) all occupy the slot.
          if (
            appointment.status &&
            appointment.status !== "NOT_STARTED" &&
            appointment.status !== "STARTED" &&
            appointment.status !== "COMPLETED"
          ) {
            return false;
          }
          if (appointment.rescheduledToId) return false;
          const appointmentDate = new Date(appointment.date);
          const appointmentEnd = new Date(appointment.endTime);
          return (
            isSameDay(appointmentDate, day) &&
            dateFnsIsBefore(slotStartDateTime, appointmentEnd) &&
            isAfter(slotEndDateTime, appointmentDate)
          );
        });

        if (teacherConflict || studentConflict) return false;

        // Skip past slots on today
        if (isSameDay(day, new Date())) {
          const now = format(new Date(), "HH:mm");
          if (slot.startTime < now) return false;
        }

        return true;
      });
  };

  const handleFindNextAvailable = () => {
    // Scan up to 90 days starting from the day after the currently-selected
    // day (so clicking the button repeatedly chains to the next-next, etc.).
    // Falls back to "today" when nothing is selected.
    const startDay = selectedDay ?? new Date();
    for (let i = 1; i <= 90; i++) {
      const candidate = addDays(startDay, i);
      const slots = getAvailableSlotsForDay(candidate);
      if (slots.length > 0) {
        const weekStart = startOfWeek(candidate, { weekStartsOn: 0 });
        setCurrentStartDate(weekStart);
        setCurrentMonth(startOfMonth(candidate));
        setSelectedDay(candidate);
        return;
      }
    }
  };

  const filteredAvailability = availability
    .filter((slot) => {
      if (!selectedDay) return false;
      return slot.dayOfWeek === selectedDay.getDay();
    })
    .map((slot) => {
      if (!selectedDay) return slot;

      const slotStartDateTime = new Date(
        selectedDay.getFullYear(),
        selectedDay.getMonth(),
        selectedDay.getDate(),
        ...slot.startTime.split(":").map(Number),
      );
      const slotEndDateTime = new Date(
        selectedDay.getFullYear(),
        selectedDay.getMonth(),
        selectedDay.getDate(),
        ...slot.endTime.split(":").map(Number),
      );

      const teacherConflict = appointments.some((appointment) => {
        const appointmentDate = new Date(appointment.date);
        const appointmentEnd = new Date(appointment.endTime);
        return (
          isSameDay(appointmentDate, selectedDay) &&
          dateFnsIsBefore(slotStartDateTime, appointmentEnd) &&
          isAfter(slotEndDateTime, appointmentDate)
        );
      });

      const studentConflict = studentAppointments.some((appointment) => {
        // Block all non-terminal statuses (see also: getAvailableSlotsForDay).
        if (
          appointment.status &&
          appointment.status !== "NOT_STARTED" &&
          appointment.status !== "STARTED" &&
          appointment.status !== "COMPLETED"
        ) {
          return false;
        }
        if (appointment.rescheduledToId) return false;
        const appointmentDate = new Date(appointment.date);
        const appointmentEnd = new Date(appointment.endTime);
        return (
          isSameDay(appointmentDate, selectedDay) &&
          dateFnsIsBefore(slotStartDateTime, appointmentEnd) &&
          isAfter(slotEndDateTime, appointmentDate)
        );
      });

      return {
        ...slot,
        isDisabled: teacherConflict || studentConflict,
        disabledReason: teacherConflict
          ? "Teacher already has another session at this time"
          : studentConflict
            ? "You already have another lesson at this time"
            : undefined,
      };
    });

  if (loading) {
    return <Loading isOpen={true} />;
  }

  if (!teacherData) {
    return <p className="mt-10 text-center">Teacher not found.</p>;
  }

  return (
    <>
      {/* Spinner */}
      {isSigningIn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Spinner isOpen={true} message="Signing in..." />
        </div>
      )}
      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="fixed bottom-7 left-1/2 z-60 w-full max-w-md -translate-x-1/2 transform px-4">
          <div className="relative flex items-center rounded-md bg-white p-4 text-black shadow-lg">
            <FaCheck className="mr-2 text-green-500" />
            <span>Appointment Confirmed Successfully! 🤩🎉</span>
            <div className="animate-progress absolute bottom-0 left-0 h-1 rounded-l-xl rounded-r-xl rounded-b-xl bg-green-500"></div>
          </div>
        </div>
      )}
      {/* Error Message */}
      {errorMessage && (
        <div className="fixed bottom-7 left-1/2 z-60 w-full max-w-xl -translate-x-1/2 transform px-4">
          <div className="relative flex items-center rounded-md bg-white p-4 text-black shadow-lg">
            {isVerificationError ? (
              <FaExclamation className="mr-2 text-yellow-500" />
            ) : (
              <FaX className="mr-2 text-red-500" />
            )}
            <span>
              {errorMessage}{" "}
              {isVerificationError && (
                <button
                  type="button"
                  onClick={resendVerificationCode}
                  data-testid="teacherProfileResendVerificationLink"
                  className="rounded-md font-semibold text-[#27335d] transition-all hover:cursor-pointer hover:text-[#5C5757]"
                >
                  Resend?
                </button>
              )}
            </span>
            <div
              className={`animate-progress absolute bottom-0 left-0 h-1 rounded-l-xl rounded-r-xl rounded-b-xl ${
                isVerificationError ? "bg-yellow-500" : "bg-red-500"
              }`}
            ></div>
          </div>
        </div>
      )}
      {/* Error Message */}
      {errorLikeMessage && (
        <div className="fixed bottom-7 left-1/2 z-60 w-fit -translate-x-1/2 transform">
          <div className="relative flex items-center rounded-md bg-white p-4 text-black shadow-lg">
            <Image
              src={"/images/logoicon.svg"}
              alt={"Rawiah Logo"}
              width={10}
              height={10}
              quality={100}
              className="me-2.5 h-auto w-6 animate-bounce"
            />
            <span>
              {errorLikeMessage}{" "}
              <span className="link ms-0.5 text-blue-700 underline">
                <Link href={"/auth"} passHref data-testid="teacherProfileLoginLink">
                  Login ?
                </Link>
              </span>
            </span>
            <div className="animate-progress absolute bottom-0 left-0 h-1 rounded-l-xl rounded-r-xl rounded-b-xl bg-[#75C9EB]"></div>
          </div>
        </div>
      )}

      <div className="overflow-hidden">
        <div>
          <div className="bg-white">
            {/* 1st Section - Teacher Info */}
            <section className="bg-[#B6CFFB]">
              <div className="rounded-b-semifull xsm:pb-5 bg-[#E9E9EA] pb-6 sm:pb-6 md:pb-12 lg:pb-14 xl:pb-16">
                <div className="rounded-b-semifull xsm:py-8 relative bg-[#27335d] py-8 text-white sm:py-10 md:py-12 lg:py-12 xl:py-14">
                  {/* <button
                    onClick={() => {}}
                    className="rounded-full border-b-4 border-[#0c0f1c] bg-[#4887F5] font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#6d9ff7] active:scale-95 xsm:px-3 xsm:py-1.5 xsm:text-sm sm:px-3 sm:py-1.5 sm:text-base md:ml-12 md:px-5 md:py-2 md:text-lg lg:ml-14 lg:px-6 lg:py-2.5 lg:text-xl xl:ml-16 xl:px-6 xl:py-3 xl:text-2xl"
                  >
                    <Link
                      href={"/find-a-teacher"}
                      passHref
                      className="flex items-center justify-center"
                    >
                      <FaAngleLeft />
                      Back
                    </Link>
                  </button> */}
                  <div className="container mx-auto mb-3 flex flex-col justify-center gap-10 px-6 py-3 md:mb-10 md:flex-row md:py-10">
                    {/* Left Side: Video Frame */}
                    {teacherData.teacherIntroVideoId && (
                    <div className="relative h-48 w-full overflow-hidden rounded-2xl border-4 border-[#4887F5] bg-[#27335D] px-4 py-5 sm:h-56 sm:w-80 md:h-72 md:w-80 lg:h-80 lg:w-96">
                        <iframe
                          className="h-full w-full rounded-xl border-[3px] border-[#EE7E74]"
                          src={`https://www.youtube.com/embed/${teacherData.teacherIntroVideoId}`}
                          title="Teacher Introduction Video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                    </div>
                    )}

                    {/* Right Side: Teacher Info */}
                    <div className="flex flex-col items-center gap-4 text-center md:items-start md:justify-between">
                      {/* Teacher Image (Now Above the Name) */}
                      <div className="relative h-16 w-16 overflow-hidden rounded-full border-[3px] border-[#4887F5] bg-[#27335D]">
                        <Image
                          src={teacherData.image || "/images/avatar.png"}
                          alt={teacherData.name}
                          width={64}
                          height={64}
                          quality={100}
                          className="rounded-full object-cover"
                          onError={(e) =>
                            (e.currentTarget.src = "/images/avatar.png")
                          }
                        />
                      </div>

                      {/* Teacher Name */}
                      <div>
                        <h1 className="text-2xl font-bold md:text-3xl lg:text-4xl">
                          {teacherData.name}
                        </h1>
                      </div>

                      {/* Quote (Fixed Width & Wrapping) */}
                      {teacherData.quote && (
                      <div className="max-w-[250px]">
                        <h1 className="text-center leading-snug font-semibold wrap-break-word whitespace-normal md:text-left md:text-base lg:text-lg xl:text-xl">
                          &ldquo;{teacherData.quote}&rdquo;
                        </h1>
                      </div>
                      )}

                      {/* Rating and Send Message Button */}
                      <div className="flex flex-row items-center gap-1 lg:gap-1.5">
                        <Image
                          src="/images/svg/star-2.svg"
                          alt="Star"
                          width={10}
                          height={10}
                          className="h-3 w-3 object-contain lg:h-4 lg:w-4"
                        />
                        <span className="text-base font-bold text-white lg:text-lg">
                          {teacherData.rating
                            ? teacherData.rating.toFixed(1)
                            : "0.0"}
                        </span>
                        <span className="text-xs font-medium text-[#DEE0E6] lg:text-sm">
                          {numberOfReviews}{" "}
                          {numberOfReviews === 1 ? "review" : "reviews"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* <div className="flex flex-col items-center">
                    <button
                      onClick={() => {}}
                      className="rounded-full border-b-4 border-[#0c0f1c] bg-[#4887F5] font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#6d9ff7] active:scale-95 xsm:px-3 xsm:py-1.5 xsm:text-sm sm:px-3 sm:py-1.5 sm:text-base md:ml-12 md:px-5 md:py-2 md:text-lg lg:ml-14 lg:px-6 lg:py-2.5 lg:text-xl xl:ml-16 xl:px-6 xl:py-3 xl:text-2xl"
                    >
                      Send Message
                    </button>
                  </div> */}
                </div>
              </div>
            </section>
            {/* Second Section - About me */}
            <section className="bg-[#B6CFFB]">
              <div className="rounded-b-semifull bg-[#E9E9EA] pb-8 sm:pb-10 md:pb-12 lg:pb-14 xl:pb-16">
                <div className="rounded-b-semifull xsm:pb-20 xsm:pt-10 bg-[#B6CFFB] pt-10 pb-20 sm:pt-11 sm:pb-24 md:pt-16 md:pb-24 lg:pt-20 lg:pb-28 xl:pt-24 xl:pb-36">
                  {/* Section Title */}
                  <div className="mx-auto mb-8 flex flex-row items-center justify-center px-5 sm:px-10 md:px-20 lg:px-24 xl:px-28">
                    <div className="text-center">
                      <h1 className="text-xl font-bold tracking-tight text-[#27335D] sm:text-2xl md:pb-8 md:text-5xl lg:text-6xl xl:text-7xl">
                        About me
                      </h1>
                    </div>
                  </div>

                  {/* About Me Card */}
                  <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
                    <div className="flex flex-col items-start space-y-4 rounded-t-3xl border-2 border-b-0 border-[#27335D] bg-white p-4 sm:flex-row sm:space-y-0 sm:space-x-4 sm:p-6 lg:space-x-6 lg:p-8 xl:space-x-8 xl:p-10">
                      {/* Teacher Info */}
                      <div className="flex w-full flex-col space-y-3 lg:space-y-4">
                        {/* Favorite Button */}
                        <div className="flex items-center justify-end space-x-2 lg:space-x-3 xl:space-x-4">
                          <div className="flex items-center justify-between space-x-1 font-bold text-black">
                            <div
                              onClick={handleCurrentTeacherFavoriteToggle}
                              data-testid="teacherProfileFavoriteButton"
                              data-favorited={isCurrentTeacherFavorite ? "true" : "false"}
                              className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                            >
                              {isCurrentTeacherFavorite ? (
                                <SolidHeartIcon className="h-6 w-6 text-red-600" />
                              ) : (
                                <OutlineHeartIcon className="h-6 w-6 text-gray-600" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Teacher Description */}
                        <p className="mt-2 text-sm font-semibold text-[#27335D] sm:text-base md:text-lg lg:text-xl">
                          {teacherData.aboutMe ||
                            "This teacher has not provided a bio yet."}
                        </p>

                        {/* Languages & Focuses */}
                        <div className="space-y-3">
                          {/* Spoken Languages */}
                          <div className="flex flex-row gap-1 text-xs text-[#667085] sm:text-sm lg:gap-1.5">
                            <Image
                              src="/images/svg/graduationcap.svg"
                              alt="Graduation Cap"
                              width={10}
                              height={10}
                              className="h-3 w-3 object-contain sm:h-4 sm:w-4"
                            />
                            <p className="font-semibold">
                              {teacherData.spokenLanguages?.length
                                ? teacherData.spokenLanguages
                                    .map((spokenLanguage) =>
                                      toReadableFormat(spokenLanguage.language),
                                    )
                                    .join(", ")
                                : "Arabic, English"}
                            </p>
                          </div>

                          {/* Focuses */}
                          <div className="flex flex-row gap-1 text-xs text-[#667085] sm:text-sm lg:gap-1.5">
                            <Image
                              src="/images/svg/rosette.svg"
                              alt="Rosette"
                              width={10}
                              height={10}
                              className="h-3 w-3 object-contain sm:h-4 sm:w-4"
                            />
                            <p className="font-semibold">
                              {teacherData.focuses?.length
                                ? teacherData.focuses
                                    .map((focus) =>
                                      toReadableFormat(focus.name),
                                    )
                                    .join(", ")
                                : "Basics"}
                            </p>
                          </div>

                          {/* Teaching Levels */}
                          <div className="flex flex-wrap gap-2">
                            {teacherData.teacherLevels?.length
                              ? teacherData.teacherLevels
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      [
                                        "BEGINNER",
                                        "INTERMEDIATE",
                                        "ADVANCED",
                                        "PROFICIENT",
                                      ].indexOf(a.level) -
                                      [
                                        "BEGINNER",
                                        "INTERMEDIATE",
                                        "ADVANCED",
                                        "PROFICIENT",
                                      ].indexOf(b.level),
                                  )
                                  .map((level) => (
                                    <span
                                      key={level.level}
                                      className={`rounded-full border border-[#27335D] px-3 py-1 text-xs font-semibold text-[#27335D] ${
                                        {
                                          BEGINNER: "bg-[#FCE8BD]",
                                          INTERMEDIATE: "bg-[#F9D17B]",
                                          ADVANCED: "bg-[#F6C65A]",
                                          PROFICIENT: "bg-[#fabc2d]",
                                        }[level.level] || "bg-gray-400"
                                      }`}
                                    >
                                      {toReadableFormat(level.level)}
                                    </span>
                                  ))
                              : "All Levels"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Country Badge */}
                    <div className="flex flex-col items-center rounded-b-2xl bg-[#27335D]">
                      <div className="w-full p-2 text-base font-semibold text-white sm:p-3 lg:text-lg xl:p-4 xl:text-xl">
                        <div className="flex flex-row items-center justify-center gap-4 sm:justify-between lg:gap-6 xl:gap-10">
                          <div className="flex flex-row items-center gap-2">
                            <div>
                              <Image
                                src={`/images/flags/${teacherData?.country || "WORLDWIDE"}.svg`}
                                alt={teacherData?.country || "Worldwide"}
                                width={24}
                                height={24}
                                quality={100}
                                className="rounded-md border-[2.5px] border-white"
                              />
                            </div>
                            <div>
                              <p className="text-sm whitespace-nowrap sm:text-base lg:text-lg">
                                {toReadableFormat(
                                  teacherData.country ?? "Unknown Country",
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Third Section - Real words */}
            {reviews.length > 0 && <section className="bg-[#B6CFFB]">
              <div className="rounded-b-semifull xsm:pb-5 bg-[#E9E9EA] pb-6 sm:pb-6 md:pb-14 lg:pb-16 xl:pb-20">
                <div className="rounded-b-semifull xsm:py-10 relative bg-[#27335d] py-10 text-white sm:py-12 md:py-[45px] lg:py-14 xl:py-16">
                  <div className="mx-auto mt-8 mb-12 flex flex-col items-center sm:mt-6 sm:mb-20 md:mt-10 md:mb-32 lg:ms-36 lg:flex-row lg:items-start">
                    {/* Intro */}
                    <div className="text-start leading-tight">
                      <h1 className="text-2xl font-bold sm:text-3xl md:text-6xl lg:text-7xl">
                        Real Words From Real{" "}
                        <span className="block">Students!</span>
                      </h1>
                    </div>
                  </div>
                  {/* Ratings and Filter */}
                  <div className="mx-auto flex w-[80%] flex-row items-start justify-between gap-3 px-3.5 pt-5 pb-5 md:items-center md:justify-between md:pb-20">
                    <div className="flex flex-row items-center gap-1 font-bold lg:gap-1.5">
                      <div>
                        <Image
                          src="/images/svg/star-2.svg"
                          alt="Star"
                          width={10}
                          height={10}
                          className="h-3 w-3 object-contain lg:h-4 lg:w-4"
                        />
                      </div>
                      <div className="flex flex-row items-center gap-1 lg:gap-1.5">
                        <span className="text-base font-bold text-white lg:text-lg">
                          {teacherData.rating
                            ? teacherData.rating.toFixed(1)
                            : "0.0"}
                        </span>
                        <span className="text-xs font-medium text-[#DEE0E6] lg:text-sm">
                          {numberOfReviews}{" "}
                          {numberOfReviews === 1 ? "review" : "reviews"}
                        </span>
                      </div>
                    </div>
                    {/* Sort For MD, LG AND XL Filter Reviews by Rating */}
                    <div className="hidden md:block">
                      <div className="relative max-w-fit flex-1">
                        {/* Badge for selected ratings */}
                        {selectedFilterRatings.length > 0 && (
                          <span className="absolute -top-2 -right-2 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg md:h-6 md:w-6 md:text-sm lg:h-7 lg:w-7 lg:text-base xl:h-8 xl:w-8 xl:text-lg">
                            {selectedFilterRatings.length}
                          </span>
                        )}

                        {/* Dropdown Toggle */}
                        <div
                          onClick={() => toggleFilter("sort")}
                          data-testid="teacherProfileFilterByRatingButton"
                          className="filter-dropdown flex w-full cursor-pointer items-center justify-between gap-2 rounded-3xl border-2 border-[#27335D] bg-white text-center font-semibold text-[#27335D] md:px-2 md:py-1.5 md:text-base lg:px-3 lg:py-2 lg:text-lg xl:px-4 xl:py-3 xl:text-xl"
                        >
                          <span className="text-[#27335D]">
                            <Image
                              src="/images/svg/star.svg"
                              alt="Star"
                              width={16}
                              height={16}
                              quality={100}
                              className="md:h-4 md:w-4 lg:h-5 lg:w-5"
                            />
                          </span>
                          Filter By Rating
                          <span
                            className={`transform text-[#AD9F7F] transition-transform duration-200 ${
                              filters.sort ? "rotate-180" : "rotate-0"
                            }`}
                          >
                            <IoIosArrowDown />
                          </span>
                        </div>

                        {/* Dropdown Options */}
                        {filters.sort && (
                          <div className="filter-dropdown absolute z-10 mt-2 w-full rounded-3xl border-2 border-[#27335D] bg-white shadow-lg">
                            {[5, 4, 3, 2, 1].map((rating, index) => (
                              <div key={rating}>
                                <label
                                  htmlFor={`rating-${rating}`}
                                  className="flex cursor-pointer items-center gap-2.5 px-2 py-0.5 font-semibold text-[#27335D] md:px-2.5 md:py-1 md:text-base lg:px-3 lg:py-1 lg:text-lg xl:px-4 xl:py-2 xl:text-xl"
                                >
                                  <input
                                    type="checkbox"
                                    id={`rating-${rating}`}
                                    data-testid={`teacherProfileRatingCheckbox${rating}`}
                                    checked={selectedFilterRatings.includes(
                                      rating,
                                    )}
                                    onChange={() =>
                                      setSelectedFilterRatings((prev) =>
                                        prev.includes(rating)
                                          ? prev.filter(
                                              (item) => item !== rating,
                                            )
                                          : [...prev, rating],
                                      )
                                    }
                                    className="cursor-pointer appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 xl:h-5 xl:w-5"
                                  />
                                  {rating} Stars
                                </label>
                                {index < 4 && (
                                  <hr className="mx-auto border-t-[1.5px] border-[#27335D]" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sort For XSM, SM .. ETC (Filter Reviews by Rating) */}
                    <div className="block md:hidden">
                      <div className="relative flex-1">
                        {/* Badge for selected ratings */}
                        {selectedFilterRatings.length > 0 && (
                          <span className="absolute -top-2 -right-2 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-[#F6C65A] text-xs font-bold text-[#27335D] shadow-lg">
                            {selectedFilterRatings.length}
                          </span>
                        )}

                        {/* Dropdown Toggle */}
                        <div
                          onClick={() => toggleFilter("sort")}
                          data-testid="teacherProfileFilterByRatingButtonMobile"
                          className="filter-dropdown flex items-center justify-center rounded-xl border-2 border-[#27335D] bg-white p-2 text-center font-semibold text-[#27335D]"
                        >
                          <span className="text-[#27335D]">
                            <Image
                              src="/images/svg/star.svg"
                              alt="Star"
                              width={10}
                              height={10}
                              className="h-3 w-3 object-contain"
                            />
                          </span>
                        </div>

                        {/* Dropdown Options */}
                        {filters.sort && (
                          <div className="filter-dropdown absolute right-0 z-10 mt-2 w-40 rounded-xl border-2 border-[#27335D] bg-white shadow-lg">
                            {[5, 4, 3, 2, 1].map((rating, index) => (
                              <div key={rating}>
                                <label
                                  htmlFor={`rating-xsm-${rating}`}
                                  className="flex cursor-pointer items-center gap-2 px-3 py-1 text-sm font-semibold text-[#27335D]"
                                >
                                  <input
                                    type="checkbox"
                                    id={`rating-xsm-${rating}`}
                                    data-testid={`teacherProfileRatingCheckboxMobile${rating}`}
                                    checked={selectedFilterRatings.includes(
                                      rating,
                                    )}
                                    onChange={() =>
                                      setSelectedFilterRatings((prev) =>
                                        prev.includes(rating)
                                          ? prev.filter(
                                              (item) => item !== rating,
                                            )
                                          : [...prev, rating],
                                      )
                                    }
                                    className="cursor-pointer h-3.5 w-3.5 appearance-none rounded border-[3px] border-[#F6C65A] checked:border-[#F6C65A] checked:bg-[#FCE8BD] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22%2327335D%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%200%201%200%201.414l-5%205a1%201%200%200%201-1.414%200l-2-2a1%201%200%200%201%201.414-1.414L6.5%209.086l4.293-4.293a1%201%200%200%201%201.414%200z%22/%3E%3C/svg%3E')] checked:bg-center checked:bg-no-repeat focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4887F5] focus-visible:ring-offset-1"
                                  />
                                  {rating} Stars
                                </label>
                                {index < 4 && (
                                  <hr className="mx-auto border-t-[1.5px] border-[#27335D]" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Reviews Grid */}
                  <div className="mx-auto mb-24 grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
                    {filteredReviews.length > 0 ? (
                      filteredReviews.map((review) => (
                        <div
                          key={review.id}
                          className="mx-auto flex h-full min-h-32 w-full max-w-[85%] flex-col gap-y-3 rounded-3xl border-2 border-[#4887F5] bg-white px-3 py-4 sm:max-w-xs md:w-full md:max-w-[90%] lg:w-full"
                        >
                          <div className="text-lg font-semibold text-black">
                            {review.text}
                          </div>
                          <div className="flex justify-between">
                            <div className="text-sm font-bold text-[#4887F5]">
                              {review.student.name}
                            </div>
                            <div className="text-sm font-bold text-[#667085]">
                              {new Date(review.date).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: review.rating }, (_, i) => (
                              <Image
                                key={i}
                                src="/images/svg/star.svg"
                                alt="Star"
                                width={16}
                                height={16}
                                quality={100}
                                className="h-4 w-4"
                              />
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-1 mt-10 text-center text-lg font-semibold text-white md:col-span-2">
                        No reviews.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>}

            {/* Fourth Section - Book me */}
            <section id="book" className="bg-[#27335D]">
              <div className="rounded-b-semifull xsm:pb-8 bg-[#E9E9EA] pb-8 sm:pb-10 md:pb-12 lg:pb-14 xl:pb-16">
                <div className="rounded-b-semifull xsm:py-12 bg-[#B6CFFB] py-12 sm:py-14 md:py-16 lg:py-20 xl:py-24">
                  <div className="xsm:px-5 mx-auto mb-8 flex flex-row items-center justify-center px-5 sm:px-10 md:px-20 lg:px-24 xl:px-28">
                    <div className="text-center">
                      <h1 className="xsm:text-xl pb-3 text-xl font-bold tracking-tight text-[#27335D] sm:text-2xl md:pb-8 md:text-5xl lg:text-6xl xl:text-7xl">
                        Book Me Before I&apos;m{" "}
                        <span className="block"> Fully Booked!</span>
                      </h1>
                    </div>
                  </div>
                  {/* Payout-pending note — booking still allowed (soft-gate). */}
                  {teacherData && teacherData.payoutConfigured === false && (
                    <div
                      className="mx-auto mb-6 max-w-2xl rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-800"
                      data-testid="teacherPayoutPendingNotice"
                    >
                      This teacher is finalising their payment setup. Bookings
                      are allowed — please contact support if you have any
                      questions.
                    </div>
                  )}
                  {/* Booking Component */}
                  <BookingComponent
                    currentMonth={currentMonth}
                    setCurrentMonth={setCurrentMonth}
                    currentStartDate={currentStartDate}
                    handlePreviousWeek={handlePreviousWeek}
                    handleNextWeek={handleNextWeek}
                    handlePreviousDay={handlePreviousDay}
                    handleNextDay={handleNextDay}
                    selectedDay={selectedDay}
                    selectedTimeSlot={selectedTimeSlot}
                    filteredAvailability={filteredAvailability}
                    availabilityLoading={availabilityLoading}
                    handlePreviousMonth={handlePreviousMonth}
                    handleNextMonth={handleNextMonth}
                    setSelectedDay={setSelectedDay}
                    setSelectedTimeSlot={setSelectedTimeSlot}
                    handleConfirmBooking={handleBookingFlow}
                    formatTime={formatTime}
                    setCurrentStartDate={setCurrentStartDate}
                    onFindNextAvailable={handleFindNextAvailable}
                  />
                </div>
              </div>
            </section>

            {/* Fifth Section - Resume */}
            <section className="bg-[#B6CFFB]">
              <div className="rounded-b-semifull xsm:pb-5 bg-[#E9E9EA] pb-6 sm:pb-6 md:pb-10 lg:pb-12 xl:pb-14">
                <div className="rounded-b-semifull xsm:py-10 relative bg-[#27335d] py-10 text-white sm:py-12 md:py-[45px] lg:py-14 xl:py-16">
                  <div className="mx-auto mt-8 mb-12 flex flex-col items-center sm:mt-6 sm:mb-20 md:mt-10 md:mb-32 lg:ms-36 lg:flex-row lg:items-start">
                    {/* Intro */}
                    <div className="text-start leading-tight">
                      <h1 className="text-3xl font-bold sm:text-4xl md:text-6xl lg:text-7xl">
                        Resume
                      </h1>
                    </div>
                  </div>
                  <div className="mb-36 flex flex-col items-center justify-center gap-10 md:flex-row">
                    <div>
                      {/* Container for Image and Info */}
                      <div className="mx-auto">
                        <div className="flex flex-row items-start space-x-4 rounded-t-3xl border-2 border-b-0 border-[#4887F5] bg-white p-4">
                          {/* Teacher Info */}
                          <div className="flex flex-col space-y-3 lg:space-y-4">
                            <div className="flex items-center justify-start space-x-2 lg:space-x-3 xl:space-x-4">
                              <div className="flex items-center justify-between space-x-1 font-bold text-black">
                                <Image
                                  src="/images/svg/rosette2.svg"
                                  alt="Rosette"
                                  width={10}
                                  height={10}
                                  className="h-8 w-8 object-contain"
                                />
                              </div>
                            </div>
                            <p className="pb-10 text-xl font-semibold text-[#27335D]">
                              Certified Arabic teacher
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-center rounded-b-2xl bg-[#4887F5]">
                          <div className="w-full px-2 py-2 text-base font-semibold text-white lg:px-3 lg:text-lg xl:px-4 xl:text-xl">
                            <div className="flex flex-row items-center justify-between gap-4 lg:gap-6 xl:gap-10">
                              <div className="flex flex-row items-center gap-2">
                                <p className="text-base font-semibold text-[#BDBDBD]">
                                  December 4, 2020
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      {/* Container for Image and Info */}
                      <div className="mx-auto">
                        <div className="flex flex-row items-start space-x-4 rounded-t-3xl border-2 border-b-0 border-[#4887F5] bg-white p-4">
                          {/* Teacher Info */}
                          <div className="flex flex-col space-y-3 lg:space-y-4">
                            <div className="flex items-center justify-start space-x-2 lg:space-x-3 xl:space-x-4">
                              <div className="flex items-center justify-between space-x-1 font-bold text-black">
                                <Image
                                  src="/images/svg/rosette2.svg"
                                  alt="Rosette"
                                  width={10}
                                  height={10}
                                  className="h-8 w-8 object-contain"
                                />
                              </div>
                            </div>
                            <p className="pb-10 text-xl font-semibold text-[#27335D]">
                              Certified Arabic teacher
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-center rounded-b-2xl bg-[#4887F5]">
                          <div className="w-full px-2 py-2 text-base font-semibold text-white lg:px-3 lg:text-lg xl:px-4 xl:text-xl">
                            <div className="flex flex-row items-center justify-between gap-4 lg:gap-6 xl:gap-10">
                              <div className="flex flex-row items-center gap-2">
                                <p className="text-base font-semibold text-[#BDBDBD]">
                                  December 4, 2020
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Sixth Section - Might like */}
            <section>
              <div className="rounded-b-semifull xsm:pb-8 bg-[#E9E9EA] pb-8 sm:pb-10 md:pb-12 lg:pb-14 xl:pb-16">
                <div className="rounded-b-semifull xsm:pb-16 xsm:pt-12 bg-[#B6CFFB] pt-12 pb-16 sm:pt-14 sm:pb-20 md:pt-16 md:pb-24 lg:pt-20 lg:pb-28 xl:pt-24 xl:pb-32">
                  <div className="xsm:px-5 mx-auto mb-8 flex flex-row items-center justify-center px-5 sm:px-10 md:px-20 lg:px-24 xl:px-28">
                    <div className="text-center">
                      <h1 className="xsm:text-xl pb-3 text-xl font-bold tracking-tight text-[#27335D] sm:text-2xl md:pb-8 md:text-5xl lg:text-6xl xl:text-7xl">
                        You might also like
                      </h1>
                    </div>
                  </div>
                  <div className="">
                    <div className="flex flex-wrap items-center justify-center gap-6">
                      {otherTeachers.length > 0 ? (
                        otherTeachers.slice(0, 4).map((teacher) => (
                          <div key={teacher.id} className="w-68">
                            {/* Teacher Card */}
                            <div className="flex flex-col items-start rounded-t-3xl border-2 border-b-0 border-[#27335D] bg-white p-4">
                              <div className="relative h-36 w-full overflow-hidden rounded-xl border border-[#27335D]">
                                <Image
                                  src={teacher.image || "/images/avatar.png"}
                                  alt={teacher.name || "Teacher"}
                                  layout="fill"
                                  objectFit="cover"
                                  className="transition-transform duration-300"
                                  quality={100}
                                  onError={(e) =>
                                    (e.currentTarget.src = "/images/avatar.png")
                                  }
                                />
                                {/* Rating */}
                                <div className="absolute top-3 left-3 flex items-center gap-1 rounded-md bg-white px-2 py-1 shadow-md">
                                  <Image
                                    src="/images/svg/star.svg"
                                    alt="Star"
                                    width={10}
                                    height={10}
                                    className="h-3 w-3 object-contain"
                                  />
                                  <span className="text-xs font-bold text-[#27335D]">
                                    {teacher.rating
                                      ? teacher.rating.toFixed(1)
                                      : "0.0"}
                                  </span>
                                </div>
                                {/* Heart Icon */}
                                <div
                                  className="absolute top-3 right-3 rounded-full bg-white p-1 shadow-md hover:cursor-pointer"
                                  data-testid="teacherProfileOtherTeacherFavoriteButton"
                                  onClick={() =>
                                    handleOtherTeacherFavoriteToggle(teacher.id)
                                  }
                                >
                                  {otherFavorites[teacher.id] ? (
                                    <SolidHeartIcon className="h-5 w-5 text-red-600" />
                                  ) : (
                                    <OutlineHeartIcon className="h-5 w-5 text-gray-600" />
                                  )}
                                </div>
                              </div>
                              <div className="mt-3 flex w-full flex-col space-y-2">
                                <h2 className="text-base font-semibold text-[#27335d]">
                                  {teacher.name || "N/A"}
                                </h2>
                                {/* Languages */}
                                <div className="flex flex-row gap-1 text-xs text-[#667085]">
                                  <Image
                                    src="/images/svg/graduationcap2.svg"
                                    alt="Graduation Cap"
                                    width={10}
                                    height={10}
                                    className="h-3 w-3 object-contain"
                                  />
                                  <p className="font-semibold">
                                    {teacher.spokenLanguages?.length
                                      ? teacher.spokenLanguages
                                          .map((spokenLanguage) =>
                                            toReadableFormat(
                                              spokenLanguage.language,
                                            ),
                                          )
                                          .join(", ")
                                      : "N/A"}
                                  </p>
                                </div>
                                {/* Focuses */}
                                <div className="flex flex-row gap-1 text-xs text-[#667085]">
                                  <Image
                                    src="/images/svg/rosette3.svg"
                                    alt="Rosette"
                                    width={10}
                                    height={10}
                                    className="h-3 w-3 object-contain"
                                  />
                                  <p className="font-semibold">
                                    {teacher.focuses?.length
                                      ? teacher.focuses
                                          .map((focus) =>
                                            toReadableFormat(focus.name),
                                          )
                                          .join(", ")
                                      : ""}
                                  </p>
                                </div>
                              </div>
                            </div>
                            {/* Actions Section */}
                            <div className="flex flex-col items-end rounded-b-2xl bg-[#27335D] pe-5">
                              <div className="my-4"></div>
                            </div>
                            {/* View More */}
                            <div className="relative z-10 flex flex-col items-center">
                              <button
                                className="shadow-custom m-auto rounded-3xl rounded-t-none border-2 border-[#27335D] bg-[#4887F5] px-6 pt-0.5 pb-1 text-base font-bold text-[#27335D] transition duration-150 ease-in-out hover:bg-[#98d6f0] active:bg-[#f7c65a]"
                                data-testid="teacherProfileViewMoreButton"
                                onClick={() =>
                                  (window.location.href = `/find-a-teacher/${teacher.profileLink}`)
                                }
                              >
                                View More
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-3xl font-bold text-[#27335D]">
                          No other teachers available.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <ConfirmBookingModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          teacherData={teacherData}
          selectedDay={selectedDay}
          selectedTimeSlot={selectedTimeSlot}
          availability={availability}
          handleConfirmBooking={handleConfirmBooking}
          calculateTotalCredits={calculateTotalCredits}
          isLoading={bookingInProgress}
        />
      )}

      <PackageLoginModal
        isOpen={showLoginModal}
        closeModal={() => setShowLoginModal(false)}
      />

      <Dialog open={showSuccessModal} onOpenChange={(open) => { if (!open) setShowSuccessModal(false); }}>
        <DialogContent className="max-w-lg rounded-3xl bg-white p-0 [&>button:last-child]:hidden" data-testid="bookingSuccessModal">
          <div className="flex flex-col items-start justify-center rounded-t-3xl bg-[#27335D] px-6 py-4 text-xl font-bold text-white">
            <div className="mx-auto w-[70%]">
              <Image
                src="/images/RawiahLogos/headerlogo.svg"
                alt="Rawiah"
                width={100}
                height={100}
                unoptimized
                priority={true}
                loading="eager"
                className="mb-5 w-24"
              />
              <h2 data-testid="bookingSuccessMessage" className="text-center">
                Your session has been successfully confirmed!
              </h2>
            </div>
          </div>
          {/* Content */}
          <div className="flex flex-col items-center justify-center rounded-b-3xl border-8 border-[#27335D] bg-white p-8 pt-5">
            <div className="relative flex h-80 w-96 flex-col items-end justify-end rounded-3xl border-8 border-[#27335D] bg-[#4887F5]">
              <Image
                src="/images/svg/clickcheck2.svg"
                alt="clickcheck2"
                width={100}
                height={100}
                unoptimized
                priority={true}
                loading="eager"
                className="absolute right-1 -bottom-[10px] w-64"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
